// Country and subdivision data for the billing-address form.
//
// The billing address is a TAX input: valoi-billing replays it to Stripe Tax on every
// top-up, so a value that merely looks right produces either a refused calculation
// (409 billing_address_invalid — retrying can never fix it) or, worse, a plausible
// wrong jurisdiction that changes the tax owed without any error at all. Free-text
// country/state fields invited both. These lists exist so the form can only emit
// canonical codes.
//
// Codes are shipped, names are not: `Intl.supportedValuesOf('region')` does not exist
// (Node 22 throws `Invalid key : region`), but `Intl.DisplayNames` resolves a code to
// its localized name everywhere we run. So the only hand-maintained data is the code
// lists themselves.

export type CountryOption = { code: string; name: string };

// ISO-3166-1 alpha-2, the officially assigned codes. Order here is irrelevant —
// countryOptions() sorts by display name.
export const COUNTRY_CODES: readonly string[] = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ',
  'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CW',
  'CX', 'CY', 'CZ',
  'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ',
  'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET',
  'FI', 'FJ', 'FK', 'FM', 'FO', 'FR',
  'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT',
  'GU', 'GW', 'GY',
  'HK', 'HM', 'HN', 'HR', 'HT', 'HU',
  'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT',
  'JE', 'JM', 'JO', 'JP',
  'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ',
  'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY',
  'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS',
  'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ',
  'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ',
  'OM',
  'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY',
  'QA',
  'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ',
  'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ',
  'UA', 'UG', 'UM', 'US', 'UY', 'UZ',
  'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU',
  'WF', 'WS',
  'YE', 'YT',
  'ZA', 'ZM', 'ZW',
];

function defaultResolveName(code: string): string {
  // Deliberately unguarded. Both things here that can throw — constructing
  // Intl.DisplayNames on a runtime without full ICU, and resolving a code — are
  // caught once, in buildCountryOptions below. Catching in both places made the
  // outer guard unreachable in production, so the only thing exercising it was a
  // test injecting its own throwing resolver: a fallback verified on a path no
  // user ever takes.
  //
  // `of()` returning undefined for a valid-but-unknown region is not an exception,
  // so that still needs handling right here.
  const names = new Intl.DisplayNames(['en'], { type: 'region' });
  return names.of(code) ?? code;
}

// Exported for the fallback test; product code calls countryOptions() below.
export function buildCountryOptions(
  resolveName: (code: string) => string = defaultResolveName,
): CountryOption[] {
  return COUNTRY_CODES.map((code) => {
    // The single fallback layer, and now the one production actually goes
    // through: a runtime without full ICU must degrade to a usable dropdown of
    // bare codes rather than crashing the page that takes payment. `of()` already
    // returns the code for a valid-but-unknown region, so the degraded path and
    // the normal one converge on the same value.
    let name: string;
    try {
      name = resolveName(code);
    } catch {
      name = code;
    }
    return { code, name };
  }).sort((a, b) => a.name.localeCompare(b.name, 'en'));
}

let memoizedOptions: CountryOption[] | null = null;

/** The country dropdown's options, sorted by localized name. Memoized — the list is
 * immutable for the life of the page, and re-deriving ~250 display names per render
 * would be pure waste. */
export function countryOptions(): CountryOption[] {
  if (memoizedOptions === null) memoizedOptions = buildCountryOptions();
  return memoizedOptions;
}

// Subdivision lists ONLY for countries where Stripe Tax resolves jurisdiction by
// subdivision code — i.e. where a free-text typo silently changes the tax owed.
// Everywhere else the form keeps free text: inventing canonical lists for ~200
// countries is a maintenance liability we would get wrong, and nothing downstream
// consumes them. Adding a country here is a data change, not a code change.
//
// Codes are the postal/ISO-3166-2 suffixes Stripe expects (the verified staging
// top-up carved NY tax from exactly this form of value).
export const SUBDIVISIONS: Record<string, readonly CountryOption[]> = {
  US: [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
    // Inhabited territories — real US addresses with real (mostly zero-rate) tax
    // treatment; excluding them would lock those users out of the dropdown.
    { code: 'AS', name: 'American Samoa' },
    { code: 'GU', name: 'Guam' },
    { code: 'MP', name: 'Northern Mariana Islands' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'VI', name: 'U.S. Virgin Islands' },
  ],
  CA: [
    { code: 'AB', name: 'Alberta' },
    { code: 'BC', name: 'British Columbia' },
    { code: 'MB', name: 'Manitoba' },
    { code: 'NB', name: 'New Brunswick' },
    { code: 'NL', name: 'Newfoundland and Labrador' },
    { code: 'NT', name: 'Northwest Territories' },
    { code: 'NS', name: 'Nova Scotia' },
    { code: 'NU', name: 'Nunavut' },
    { code: 'ON', name: 'Ontario' },
    { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' },
    { code: 'SK', name: 'Saskatchewan' },
    { code: 'YT', name: 'Yukon' },
  ],
};

/** Subdivisions for a country, or null meaning "free-text field". The single lookup
 * the address form branches on. */
export function subdivisionsFor(countryCode: string): readonly CountryOption[] | null {
  return SUBDIVISIONS[countryCode] ?? null;
}

/** Field label for the subdivision, in the country's own vocabulary — "State" is
 * wrong wording in most of the world. */
export function subdivisionLabel(countryCode: string): string {
  if (countryCode === 'US') return 'State';
  if (countryCode === 'CA') return 'Province';
  return 'State / Region';
}
